import React, { useState, useEffect } from 'react';
import { 
  Container, Typography, Paper, Box, Grid, Card, 
  CardContent, CircularProgress, Button, Divider, Stack
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Plot from 'react-plotly.js';
import { 
  getProjectSummary, 
  getProjectStatusChart,
  getProjectsOverviewGanttChart,
  ProjectSummary,
  ChartData, 
} from '../api/projectAnalyticsService';
import { useAuth } from '../context/AuthContext';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import TimelineIcon from '@mui/icons-material/Timeline';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

const ProjectDashboardPage: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [authChecking, setAuthChecking] = useState(true);
  
  const [summary, setSummary] = useState<ProjectSummary | null>(null);
  const [statusChart, setStatusChart] = useState<ChartData | null>(null);
  const [projectsOverviewGantt, setProjectsOverviewGantt] = useState<ChartData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      if (user === null) return;
      navigate('/unauthorized');
    } else if (user && !['admin', 'editor'].includes(user.role)) {
      navigate('/unauthorized');
    }
    setAuthChecking(false);
  }, [isAuthenticated, user, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [summaryData, statusChartData, ganttData] = await Promise.all([
          getProjectSummary(),
          getProjectStatusChart(),
          getProjectsOverviewGanttChart()
        ]);
        setSummary(summaryData);
        setStatusChart(statusChartData);
        setProjectsOverviewGantt(ganttData);
        setError(null);
      } catch (err: any) {
        setError(err.message || 'Failed to load project analytics data');
      } finally {
        setLoading(false);
      }
    };
    if (!authChecking) fetchData();
  }, [authChecking]);
  
  if (authChecking || loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} thickness={4} />
        <Typography sx={{ mt: 2 }} color="text.secondary">{authChecking ? 'Checking permissions...' : 'Loading Zandrigo metrics...'}</Typography>
      </Box>
    );
  }

  if (error) return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 4, border: '1px solid', borderColor: 'error.light' }}>
        <Typography color="error" variant="h5" gutterBottom>Error</Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>{error}</Typography>
        <Button variant="contained" onClick={() => window.location.reload()}>Retry</Button>
      </Paper>
    </Container>
  );

  const stats = [
    { label: 'Total Projects', value: summary?.total_projects || 0, icon: <AssessmentIcon />, color: 'primary.main' },
    { label: 'On Track', value: summary?.on_track || 0, icon: <CheckCircleIcon />, color: 'success.main' },
    { label: 'At Risk', value: summary?.at_risk || 0, icon: <WarningIcon />, color: 'warning.main' },
    { label: 'Delayed', value: summary?.delayed || 0, icon: <ErrorIcon />, color: 'error.main' }
  ];

  return (
    <Container maxWidth={false} sx={{ maxWidth: '1800px', mx: 'auto', px: { xs: 2, sm: 3, md: 4 } }}>
      <Box sx={{ mb: 8, textAlign: 'center' }}>
        <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 900, letterSpacing: '-0.02em' }}>
          Zandrigo Dashboard
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
          High-level portfolio performance and integrated timelines
        </Typography>
      </Box>
      
      {/* Summary Cards - Perfectly Centered */}
      <Grid container spacing={4} sx={{ mb: 10, justifyContent: 'center' }}>
        {stats.map((stat, i) => (
          <Grid {...{ item: true, xs: 12, sm: 6, md: 3 }} key={i}>
            <Card elevation={0} sx={{ borderRadius: 5, border: '1px solid', borderColor: 'divider', height: '100%', transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' } }}>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <Box sx={{ color: stat.color, mb: 3 }}>
                  {React.cloneElement(stat.icon as React.ReactElement, { sx: { fontSize: 72 } } as any)}
                </Box>
                <Typography variant="h1" sx={{ fontWeight: 900, mb: 1, fontSize: { xs: '3rem', md: '4rem' } }}>{stat.value}</Typography>
                <Typography variant="subtitle1" color="text.secondary" sx={{ fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.2em' }}>
                  {stat.label}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={5} sx={{ justifyContent: 'center' }}>
        {/* Timeline Gantt - Expanded Width */}
        <Grid {...{ item: true, xs: 12 }}>
          <Paper elevation={0} sx={{ p: 5, borderRadius: 5, border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 5 }}>
              <CalendarMonthIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
              <Typography variant="h4" sx={{ fontWeight: 800 }}>Portfolio Timeline</Typography>
            </Box>
            <Box sx={{ width: '100%', overflowX: 'auto', overflowY: 'hidden', py: 2 }}>
              <Box sx={{ minWidth: 1000 }}>
                {projectsOverviewGantt ? (
                  <Plot 
                    data={projectsOverviewGantt.data} 
                    layout={{ 
                      ...projectsOverviewGantt.layout,
                      title: '', // Remove internal title
                      autosize: true,
                      height: 550,
                      margin: { l: 220, r: 40, t: 20, b: 80 }, // Reduced bottom margin as range slider is gone
                      showlegend: true,
                      legend: { orientation: 'h', y: -0.15, x: 0.5, xanchor: 'center' },
                      paper_bgcolor: 'rgba(0,0,0,0)',
                      plot_bgcolor: 'rgba(0,0,0,0)',
                      xaxis: { 
                        ...projectsOverviewGantt.layout?.xaxis, 
                        automargin: true, 
                        tickfont: { size: 12 },
                        rangeslider: { visible: false } // Hide zoom bars (range slider)
                      },
                      yaxis: { ...projectsOverviewGantt.layout?.yaxis, automargin: true, tickfont: { size: 14, weight: 'bold' } }
                    }} 
                    style={{ width: '100%', height: '550px' }}
                    useResizeHandler 
                    config={{ displaylogo: false, responsive: true }}
                  />
                ) : (
                  <Box sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography color="text.secondary" variant="h6">Preparing timeline data...</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Task Health - Massive Impact Card - Centered */}
        <Grid {...{ item: true, xs: 12, lg: 10 }}>
          <Paper elevation={0} sx={{ p: 6, borderRadius: 5, border: '1px solid', borderColor: 'divider', overflow: 'visible' }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 800, mb: 6, textAlign: 'center' }}>Portfolio Execution Health</Typography>
            {summary?.task_stats ? (
              <Grid container spacing={6} alignItems="center" justifyContent="center">
                <Grid {...{ item: true, xs: 12, md: 4, lg: 3 }}>
                  <Box sx={{ p: 4, m: 1, bgcolor: 'primary.main', color: 'white', borderRadius: 5, textAlign: 'center', boxShadow: '0 20px 40px -10px rgba(37, 99, 235, 0.3)' }}>
                    <Typography variant="h1" fontWeight={900} sx={{ fontSize: '4rem', mb: 1 }}>{summary.task_stats.completion_percentage}%</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, opacity: 0.9, textTransform: 'uppercase', letterSpacing: '0.15em', fontSize: '1rem' }}>Target Met</Typography>
                  </Box>
                </Grid>
                <Grid {...{ item: true, xs: 12, md: 8, lg: 9 }}>
                  <Grid container spacing={3}>
                    {Object.entries(summary.task_stats.by_status).map(([status, count]) => (
                      <Grid {...{ item: true, xs: 12, sm: 6, md: 3 }} key={status}>
                        <Box sx={{ p: 3, bgcolor: 'background.default', border: '1px solid', borderColor: 'divider', borderRadius: 4, textAlign: 'center' }}>
                          <Typography variant="h4" fontWeight={900} color="text.primary" sx={{ mb: 0.5 }}>{count as number}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 800, letterSpacing: '0.05em' }}>{status}</Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                  <Box sx={{ mt: 4 }}>
                    <Stack direction="row" spacing={4} divider={<Divider orientation="vertical" flexItem />} justifyContent="center">
                      <Box>
                        <Typography variant="h6" fontWeight={800}>{summary.task_stats.total_tasks}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>TOTAL TASKS</Typography>
                      </Box>
                      <Box>
                        <Typography variant="h6" fontWeight={800} color="success.main">{summary.task_stats.completed_tasks}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>SUCCESSFULLY CLOSED</Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Grid>
              </Grid>
            ) : (
              <Typography color="text.secondary" variant="h6" textAlign="center">Metrics loading...</Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
      
      <Box sx={{ mt: 12, display: 'flex', justifyContent: 'center', gap: 4, flexWrap: 'wrap', pb: 12 }}>
        <Button variant="contained" startIcon={<TimelineIcon />} onClick={() => navigate('/projects')} size="large" sx={{ px: 8, py: 2.5, borderRadius: 4, fontWeight: 900, fontSize: '1.2rem', boxShadow: '0 10px 15px -3px rgba(37, 99, 235, 0.4)' }}>
          Explore Portfolio
        </Button>
        <Button variant="outlined" onClick={() => navigate('/project-visualizer')} size="large" sx={{ px: 8, py: 2.5, borderRadius: 4, fontWeight: 900, fontSize: '1.2rem', borderWidth: 2, '&:hover': { borderWidth: 2 } }}>
          Interactive Visualizer
        </Button>
      </Box>
    </Container>
  );
};

export default ProjectDashboardPage;